import { CellData, RowData } from "../../../s25-spreadsheet/types/sheet.js.types.expanded";
import { ImportCustomAttribute, Organization, OrganizationMembership, OrganizationAddress } from "../../import.service";
import { S25Util } from "../../../../util/s25-util";
import { ImportUtil } from "../import.util";

export class OrganizationSpreadsheetUtil {
    public static rowsToOrganizations = (
        orgNameSet: Set<string>,
        orgTypeNameSet: Set<string>,
        catNameSet: Set<string>,
        ratingNameSet: Set<string>,
        errorSet: Set<string>,
        rows: RowData[],
    ): { organizations: Organization[] } => {
        const organizations: Organization[] = [];
        const memberships: OrganizationMembership[] = [];
        const cellNumToHeader: Map<number, string> = new Map<number, string>();
        const caColumnsByIndex = new Map<number, { name: string; dataType?: string }>();

        if (!rows || !rows.length) {
            return { organizations: [] };
        }

        for (let r = 0; r < rows.length; r++) {
            const row = rows[r];
            const cells = row?.cells;
            if (!row || !cells?.length) {
                continue;
            }

            if (r === 0) {
                // first row is headers
                const seenCaNames = new Map<string, number>();
                for (let columnIndex = 0; columnIndex < cells.length; columnIndex++) {
                    const cell = cells[columnIndex];
                    if (!cell?.text) continue;
                    const headerText = cell.text.trim();
                    if (!headerText) continue;

                    if (ImportUtil.isCustomAttributeHeader(headerText)) {
                        const parsed = ImportUtil.parseCustomAttributeHeader(headerText);
                        if (parsed) {
                            const lowerName = parsed.name.toLowerCase();
                            const priorColumn = seenCaNames.get(lowerName);
                            if (priorColumn !== undefined) {
                                errorSet.add(
                                    `Duplicate CA header "${parsed.name}" in columns ${priorColumn + 1} and ${columnIndex + 1}`,
                                );
                            } else {
                                seenCaNames.set(lowerName, columnIndex);
                                caColumnsByIndex.set(columnIndex, parsed);
                            }
                        } else {
                            errorSet.add(`Could not parse custom attribute header: "${headerText}"`);
                        }
                    } else {
                        cellNumToHeader.set(columnIndex, cell.text);
                    }
                }
                continue;
            }

            const org = this.cellsToOrganization(
                cells,
                cellNumToHeader,
                orgNameSet,
                orgTypeNameSet,
                catNameSet,
                ratingNameSet,
                errorSet,
            );

            const membership = this.cellsToMembership(cells, cellNumToHeader, orgNameSet);

            // Collect CA values from this row.
            const rowCas: ImportCustomAttribute[] = [];
            caColumnsByIndex.forEach((caMeta, columnIndex) => {
                const cellText = cells[columnIndex]?.text;
                if (cellText === undefined || cellText === null || `${cellText}`.trim() === "") {
                    return;
                }
                rowCas.push({ name: caMeta.name, dataType: caMeta.dataType, value: `${cellText}`.trim() });
            });

            // If this row has an org, create it (or find existing one)
            if (org) {
                // Check if we already have this org (by acctId or acctName)
                let existingOrg = organizations.find(
                    (existing) =>
                        (org.acctId && existing.acctId === org.acctId) ||
                        (org.acctName && existing.acctName === org.acctName),
                );

                if (!existingOrg) {
                    const orgWithMembers: Organization = { ...org, memberships: [], customAttributes: [] };
                    organizations.push(orgWithMembers);
                    existingOrg = orgWithMembers;
                }

                this.mergeCustomAttributes(existingOrg, rowCas, errorSet);

                // if this row has a membership, add it to the organization
                if (membership) {
                    if (!existingOrg.memberships) {
                        existingOrg.memberships = [];
                    }
                    existingOrg.memberships.push(membership);
                }
            } else if (membership) {
                // Row only has membership, need to find or create org
                memberships.push(membership);
            }
        }

        // Group orphaned memberships (those not in same row as org) by organization
        if (memberships.length > 0) {
            const orgMapById = new Map<number, Organization>();
            const orgMapByName = new Map<string, Organization>();

            for (const org of organizations) {
                if (org.acctId) {
                    orgMapById.set(org.acctId, org);
                }
                if (org.acctName) {
                    orgMapByName.set(org.acctName, org);
                }
            }

            // Assign orphaned memberships to their organizations
            for (const membership of memberships) {
                let targetOrg: Organization | undefined;

                if (membership.acctId) {
                    targetOrg = orgMapById.get(membership.acctId);
                } else if (membership.acctName) {
                    targetOrg = orgMapByName.get(membership.acctName);
                }

                if (targetOrg) {
                    if (!targetOrg.memberships) {
                        targetOrg.memberships = [];
                    }
                    targetOrg.memberships.push(membership);
                } else {
                    errorSet.add(
                        `Membership references organization that doesn't exist by name or id: ${membership.acctName || membership.acctId}`,
                    );
                }
            }
        }

        return { organizations };
    };

    /**
     * Drops later memberships in the same org that share email + role name.
     * Missing email or role name is left in place so required-field validation
     * can still report those rows.
     */
    public static collapseDuplicateMemberships(organizations: Organization[]): void {
        if (!organizations?.length) {
            return;
        }
        for (const org of organizations) {
            if (!org.memberships?.length) {
                continue;
            }
            const seenKeys = new Set<string>();
            const collapsed: OrganizationMembership[] = [];
            for (const membership of org.memberships) {
                const email = membership.contactEmail?.trim().toLowerCase();
                const roleName = membership.roleName?.trim();
                if (!email || !roleName) {
                    collapsed.push(membership);
                    continue;
                }
                const membershipKey = `${email}\0${roleName}`;
                if (seenKeys.has(membershipKey)) {
                    continue;
                }
                seenKeys.add(membershipKey);
                collapsed.push(membership);
            }
            org.memberships = collapsed;
        }
    }

    /**
     * Merges CA values from a later row into an existing org. Same name + same value is a no-op;
     * same name + different non-empty value is an error. Empty / missing cells are ignored.
     */
    private static mergeCustomAttributes(
        org: Organization,
        rowCas: ImportCustomAttribute[],
        errorSet: Set<string>,
    ): void {
        if (!org.customAttributes) {
            org.customAttributes = [];
        }
        for (const incoming of rowCas) {
            const lowerName = incoming.name.toLowerCase();
            const existing = org.customAttributes.find((ca) => ca.name.toLowerCase() === lowerName);
            if (existing) {
                if (existing.value !== incoming.value) {
                    errorSet.add(
                        `Organization "${org.acctName}": conflicting values for CA "${incoming.name}": "${existing.value}" vs "${incoming.value}"`,
                    );
                }
            } else {
                org.customAttributes.push(incoming);
            }
        }
    }

    private static cellsToOrganization = (
        cells: CellData[],
        cellNumToHeader: Map<number, string>,
        orgNameSet: Set<string>,
        orgTypeNameSet: Set<string>,
        catNameSet: Set<string>,
        ratingNameSet: Set<string>,
        errorSet: Set<string>,
    ): Organization | null => {
        const org: Organization = {};
        let hasData = false;
        let adminAddress: OrganizationAddress | null = null;
        let billingAddress: OrganizationAddress | null = null;

        for (let c = 0; c < cells.length; c++) {
            const rawHeader = cellNumToHeader.get(c);
            if (!rawHeader || !cells[c]?.text) continue;

            let value = cells[c].text.trim();
            if (!value) continue;

            hasData = true;
            const normalizedHeader = ImportUtil.normalize(rawHeader);
            const normalizedRawHeader = rawHeader.toLowerCase();

            // Organization ID / Account ID
            if (
                normalizedHeader === ImportUtil.normalize("organization_id") ||
                normalizedHeader === ImportUtil.normalize("acct_id") ||
                normalizedHeader === ImportUtil.normalize("organizationid") ||
                normalizedHeader === ImportUtil.normalize("acctid")
            ) {
                org.acctId = S25Util.parseInt(value);
            }
            // Organization Name / Account Name
            else if (
                normalizedHeader === ImportUtil.normalize("organization_name") ||
                normalizedHeader === ImportUtil.normalize("acct_name") ||
                normalizedHeader === ImportUtil.normalize("name") ||
                normalizedHeader === ImportUtil.normalize("organizationname") ||
                normalizedHeader === ImportUtil.normalize("acctname")
            ) {
                // If prefixed with $NAME-, strip prefix and use as name
                value = value.replace(/^\$NAME-/, "");
                org.acctName = value;
                orgNameSet.add(value);
            }
            // Title
            else if (
                normalizedHeader === ImportUtil.normalize("organization_title") ||
                normalizedHeader === ImportUtil.normalize("acct_title") ||
                normalizedHeader === ImportUtil.normalize("title")
            ) {
                org.acctTitle = value;
            }
            // Account Number
            else if (
                normalizedHeader === ImportUtil.normalize("organization_number") ||
                normalizedHeader === ImportUtil.normalize("acct_number") ||
                normalizedHeader === ImportUtil.normalize("number") ||
                normalizedHeader === ImportUtil.normalize("account_number") ||
                normalizedHeader === ImportUtil.normalize("accountnumber")
            ) {
                org.acctNumber = value;
            }
            // Contact Name
            else if (normalizedHeader === ImportUtil.normalize("contact_name")) {
                org.contactName = value;
            }
            // S25 Name
            else if (
                normalizedHeader === ImportUtil.normalize("s25_name") ||
                normalizedHeader === ImportUtil.normalize("s25name")
            ) {
                org.s25Name = value;
            }
            // Type / Organization Type
            else if (
                normalizedHeader === ImportUtil.normalize("org_type_id") ||
                normalizedHeader === ImportUtil.normalize("type_id") ||
                normalizedHeader === ImportUtil.normalize("org_type_name") ||
                normalizedHeader === ImportUtil.normalize("type_name") ||
                normalizedHeader === ImportUtil.normalize("organization_type") ||
                normalizedHeader === ImportUtil.normalize("type")
            ) {
                const parsed = ImportUtil.parseIdOrName(value);
                if (parsed.id != null) {
                    org.orgTypeId = parsed.id;
                } else if (parsed.name) {
                    org.orgTypeName = parsed.name;
                    orgTypeNameSet.add(parsed.name);
                }
            }
            // Category / Categories
            else if (
                normalizedHeader === ImportUtil.normalize("CATEGORIES") ||
                normalizedHeader === ImportUtil.normalize("category") ||
                normalizedHeader === ImportUtil.normalize("categories")
            ) {
                // Parse pipe-delimited categories (similar to event import CATEGORIES handling)
                // If fully numeric, treat as category ID. Otherwise, strip $NAME- prefix if present and treat as name.
                org.categories = org.categories || [];
                org.categoryNames = org.categoryNames || [];
                const categories = org.categories;
                const categoryNames = org.categoryNames;
                S25Util.array.unique(ImportUtil.splitStr(value, "|")).forEach((c) => {
                    if (S25Util.isParsableAsInt(c)) {
                        // Fully numeric value - treat as category ID directly (no lookup needed)
                        if (!categories.find((cat) => cat.category_id === parseInt(c))) {
                            categories.push({ category_id: parseInt(c) });
                        }
                    } else {
                        // Non-numeric or numeric with $NAME- prefix - strip prefix and store name for later resolution
                        c = c.replace(/^\$NAME-/, "");
                        categoryNames.push(c);
                        catNameSet.add(c);
                    }
                });
            }
            // Rating
            else if (
                normalizedHeader === ImportUtil.normalize("acct_rating") ||
                normalizedHeader === ImportUtil.normalize("rating")
            ) {
                const parsed = ImportUtil.parseIdOrName(value);
                if (parsed.id != null) {
                    org.acctRating = parsed.id;
                } else if (parsed.name) {
                    org.ratingName = parsed.name;
                    ratingNameSet.add(parsed.name);
                }
            }
            // Prime Contact (can be contact ID or email)
            else if (
                normalizedHeader === ImportUtil.normalize("prime_contact") ||
                normalizedHeader === ImportUtil.normalize("primecontact")
            ) {
                // Support both ID and email
                if (S25Util.isParsableAsInt(value)) {
                    org.primeContact = S25Util.parseInt(value);
                } else {
                    // Store email for later resolution to ID
                    org.primeContactEmail = value;
                }
            }
            // Administrative Address Fields (addrType = 1)
            else if (normalizedRawHeader.includes("administrative")) {
                if (!adminAddress) {
                    adminAddress = { addrType: 1 };
                }
                if (normalizedRawHeader.includes("street") || normalizedRawHeader.includes("address")) {
                    adminAddress.address = value;
                } else if (normalizedRawHeader.includes("city")) {
                    adminAddress.city = value;
                } else if (normalizedRawHeader.includes("state") || normalizedRawHeader.includes("province")) {
                    adminAddress.stateProv = value;
                } else if (
                    normalizedRawHeader.includes("zip") ||
                    normalizedRawHeader.includes("postal") ||
                    normalizedRawHeader.includes("post")
                ) {
                    adminAddress.zipPostCode = value;
                } else if (normalizedRawHeader.includes("country")) {
                    adminAddress.country = value;
                } else if (normalizedRawHeader.includes("phone")) {
                    adminAddress.phone = value;
                } else if (normalizedRawHeader.includes("fax")) {
                    adminAddress.fax = value;
                }
            }
            // Billing Address Fields (addrType = 2)
            else if (normalizedRawHeader.includes("billing")) {
                if (!billingAddress) {
                    billingAddress = { addrType: 2 };
                }
                if (normalizedRawHeader.includes("street") || normalizedRawHeader.includes("address")) {
                    billingAddress.address = value;
                } else if (normalizedRawHeader.includes("city")) {
                    billingAddress.city = value;
                } else if (normalizedRawHeader.includes("state") || normalizedRawHeader.includes("province")) {
                    billingAddress.stateProv = value;
                } else if (
                    normalizedRawHeader.includes("zip") ||
                    normalizedRawHeader.includes("postal") ||
                    normalizedRawHeader.includes("post")
                ) {
                    billingAddress.zipPostCode = value;
                } else if (normalizedRawHeader.includes("country")) {
                    billingAddress.country = value;
                } else if (normalizedRawHeader.includes("phone")) {
                    billingAddress.phone = value;
                } else if (normalizedRawHeader.includes("fax")) {
                    billingAddress.fax = value;
                }
            }
        }

        // Add addresses to organization if they have any data
        if (adminAddress || billingAddress) {
            org.addresses = [];
            if (
                adminAddress &&
                (adminAddress.address ||
                    adminAddress.city ||
                    adminAddress.stateProv ||
                    adminAddress.zipPostCode ||
                    adminAddress.country ||
                    adminAddress.phone ||
                    adminAddress.fax)
            ) {
                org.addresses.push(adminAddress);
            }
            if (
                billingAddress &&
                (billingAddress.address ||
                    billingAddress.city ||
                    billingAddress.stateProv ||
                    billingAddress.zipPostCode ||
                    billingAddress.country ||
                    billingAddress.phone ||
                    billingAddress.fax)
            ) {
                org.addresses.push(billingAddress);
            }
            if (org.addresses.length === 0) {
                delete org.addresses;
            }
        }

        if (!hasData) {
            return null;
        }

        if (!org.acctName?.trim()) {
            errorSet.add("Organization name is required");
            return null;
        }

        return org;
    };

    private static cellsToMembership = (
        cells: CellData[],
        cellNumToHeader: Map<number, string>,
        orgNameSet: Set<string>,
    ): OrganizationMembership | null => {
        const membership: OrganizationMembership = {};
        let hasData = false;

        for (let c = 0; c < cells.length; c++) {
            const rawHeader = cellNumToHeader.get(c);
            if (!rawHeader || !cells[c]?.text) continue;

            let value = cells[c].text.trim();
            if (!value) continue;

            const normalizedHeader = ImportUtil.normalize(rawHeader);

            // Check if this is a membership column
            if (
                normalizedHeader === ImportUtil.normalize("membership_org_id") ||
                normalizedHeader === ImportUtil.normalize("membership_acct_id")
            ) {
                membership.acctId = S25Util.parseInt(value);
                hasData = true;
            } else if (
                normalizedHeader === ImportUtil.normalize("membership_org_name") ||
                normalizedHeader === ImportUtil.normalize("membership_acct_name") ||
                normalizedHeader === ImportUtil.normalize("membership_organization")
            ) {
                // If prefixed with $NAME-, strip prefix and use as name
                value = value.replace(/^\$NAME-/, "");
                membership.acctName = value;
                orgNameSet.add(value);
                hasData = true;
            } else if (
                normalizedHeader === ImportUtil.normalize("membership_contact_id") ||
                normalizedHeader === ImportUtil.normalize("membership_cont_id")
            ) {
                membership.contId = S25Util.parseInt(value);
                hasData = true;
            } else if (
                normalizedHeader === ImportUtil.normalize("membership_contact_email") ||
                normalizedHeader === ImportUtil.normalize("membership_email") ||
                normalizedHeader === ImportUtil.normalize("contact_email") ||
                normalizedHeader === ImportUtil.normalize("email")
            ) {
                membership.contactEmail = value;
                hasData = true;
            } else if (
                normalizedHeader === ImportUtil.normalize("membership_contact_first_name") ||
                normalizedHeader === ImportUtil.normalize("contact_first_name") ||
                normalizedHeader === ImportUtil.normalize("first_name") ||
                normalizedHeader === ImportUtil.normalize("firstname")
            ) {
                membership.contactFirstName = value;
                hasData = true;
            } else if (
                normalizedHeader === ImportUtil.normalize("membership_contact_family_name") ||
                normalizedHeader === ImportUtil.normalize("membership_contact_last_name") ||
                normalizedHeader === ImportUtil.normalize("contact_family_name") ||
                normalizedHeader === ImportUtil.normalize("contact_last_name") ||
                normalizedHeader === ImportUtil.normalize("family_name") ||
                normalizedHeader === ImportUtil.normalize("last_name") ||
                normalizedHeader === ImportUtil.normalize("familyname") ||
                normalizedHeader === ImportUtil.normalize("lastname")
            ) {
                membership.contactFamilyName = value;
                hasData = true;
            } else if (
                normalizedHeader === ImportUtil.normalize("membership_role_id") ||
                normalizedHeader === ImportUtil.normalize("membership_cont_role_id")
            ) {
                membership.contRoleId = S25Util.parseInt(value);
                hasData = true;
            } else if (
                normalizedHeader === ImportUtil.normalize("membership_role_name") ||
                normalizedHeader === ImportUtil.normalize("role_name")
            ) {
                // If prefixed with $NAME-, strip prefix and use as name
                value = value.replace(/^\$NAME-/, "");
                membership.roleName = value;
                hasData = true;
            }
        }

        if (!hasData) {
            return null;
        }

        return membership;
    };
}
